import CancelIcon from '@mui/icons-material/Cancel'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdf'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CardActionArea, CardContent, Chip, CircularProgress, Divider, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { useState, useMemo } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IContract, IPADUCompiled } from '../../definitions/Contract'
import { IContractSchema } from '../../definitions/ContractSchema'
import { IFormField } from '../../definitions/FormField'
import { IUploadedFile } from '../../definitions/Upload'
import Helper from '../../helpers/Helper'
import SmartCard from '../controls/cards/SmartCard'
import useCommand from '../hooks/useCommand'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { snackbar } from '../services/SnackbarService'
import PADUChip from '../controls/PADUChip'
import PADUEngine from '../../engines/PADUEngine'

interface P {
    contract: IContract
    contractStatus: string
    schemaStatus: string
    contractSchema: IContractSchema
}

const ContractDetailsViews = ({ contract, contractSchema }: P) => {
    const [fileLoading, setFileLoading] = useState<string>(null)
    const [copied, setCopied] = useState<boolean>(false)
    const contractId = contract?.id

    const { response: files } = useQuery({
        queryKey: buildQueryKey('files'),
        queryFn: async () => await ApiClient.getAllFiles(),
        cache: true
    })

    const { execute: executeFileDownload } = useCommand({
        commandFn: (fileKey: string) => ApiClient.readUploadedFiles([fileKey]),
        onFinish: result => {
            const fileURLs = result.response
            const signedURL = (Helper.hasVal(fileURLs) && fileURLs.length > 0) ? fileURLs[0]?.url : ''

            if (signedURL) {
                const link = document.createElement('a')
                link.href = signedURL
                link.target = '_blank'
                link.click()
            } else {
                snackbar.show({ severity: 'error', text: 'No valid URL found' })
            }
        }
    })

    const paduCompiled = useMemo(() => {
        if (!contract || !contractSchema) return null
        return PADUEngine.compile(contract, contractSchema)
    }, [contract, contractSchema])

    if (!contract && !contractSchema && !contractSchema?.steps) {
        return (
            <Tile.Container>
                <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                    <Typography variant='h6' color='text.secondary'>
                        Unable to load  Contract data
                    </Typography>
                </Box>
            </Tile.Container>
        )
    }

    if (!contractSchema && !contractSchema?.steps) {
        return (
            <Tile.Container>
                <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                    <Typography variant='h6' color='text.secondary'>
                        Unable to load Contract schema data
                    </Typography>
                </Box>
            </Tile.Container>
        )
    }

    const renderField = (field: IFormField, value: any, level: number = 0) => {
        if (field.type === 'list' && Helper.hasVal(value)) {
            return renderListField(field, value, level)
        }

        if (field.type === 'object' && Helper.hasVal(value)) {
            return renderObjectField(field, value, level)
        }

        if (field.type === 'multi-system-config' && Helper.hasVal(value)) {
            return renderMultiSystemConfig(field, value)
        }

        if (Helper.hasVal(value) && field.key === 'status') {
            return renderContractStatus(field, value)
        }

        return renderSimpleField(field, value)
    }

    const renderSimpleField = (field: IFormField, value: any) => {
        return <SimpleField paduCompiled={paduCompiled} field={field} value={value} contract={contract} />
    }

    const renderObjectField = (field: IFormField, value: any, level: number) => {
        return (
            <SmartCard key={field.key} sx={{ marginBottom: 2, marginLeft: level * 2 }}>
                <CardContent>
                    <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                        {field.label}
                    </Typography>
                    <Divider sx={{ marginBottom: 2 }} />
                    {field.objectFields?.map(objField => {
                        const relativeKey = Helper.getRelativeKey(objField.key, field.key)
                        const objValue = Helper.getNestedValue(value, relativeKey)
                        return renderField(objField, objValue, level + 1)
                    })}
                </CardContent>
            </SmartCard>
        )
    }

    const renderContractStatus = (field: IFormField, value: any) => {
        const getStatusChip = (status: string) => {
            switch (status) {
                case 'Live':
                    return <Chip label='Live' color='success' size='small' />
                case 'Draft':
                    return <Chip label='Draft' color='default' size='small' />
                case 'Archived':
                    return <Chip label='Archived' color='info' size='small' />
            }
        }

        return (
            <Box key={field.key} marginBottom={2} display='flex' flexDirection='row' alignItems='flex-start'>
                <Box flex='1' paddingRight={2}>
                    <Typography variant='body2' fontWeight='medium' color='text.secondary'>
                        {field.label}:
                    </Typography>
                </Box>
                <Box flex='2'>
                    {getStatusChip(value.name)}
                </Box>
            </Box>
        )
    }
    const handleCopyBusinessKey = () => {
        setCopied(true)
        navigator.clipboard.writeText(contract?.externalId || '')
        snackbar.show({ severity: 'success', text: 'Business Key copied to clipboard' })
        setTimeout(() => setCopied(false), 3000)
    }

    const handleCopyID = () => {
        setCopied(true)
        navigator.clipboard.writeText(contract?.id.toString() ?? '')
        snackbar.show({ severity: 'success', text: 'Contract ID copied to clipboard' })
        setTimeout(() => setCopied(false), 3000)
    }

    const renderListField = (field: IFormField, value: any[], level: number) => {
        return (
            <SmartCard key={field.key} sx={{ marginBottom: 2, marginLeft: level * 2 }}>
                <CardContent>
                    <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                        {field.label} ({value.length} items)
                    </Typography>
                    {value.map((item, index) => (
                        <Accordion key={`${field.key}-${index}`} variant='outlined' sx={{ marginBottom: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant='body2'>
                                    {field?.label} #{index + 1}
                                    {item?.name && ` - ${item.name}`}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {field.childFields?.map(childField => {
                                    const relativeKey = Helper.getRelativeKey(childField.key, field.key)
                                    const childValue = Helper.getNestedValue(item, relativeKey)
                                    return renderField(childField, childValue, level + 1)
                                })}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </CardContent>
            </SmartCard>
        )
    }

    const renderMultiSystemConfig = (field: IFormField, value: any) => {
        let items = []

        if (Array.isArray(value)) {
            items = value
        } else if (value && typeof value === 'object') {
            items = [value]
        } else if (value !== null && value !== undefined) {
            items = [value]
        }

        if (items.length === 0) {
            return renderSimpleField(field, value)
        }

        return (
            <Box key={field.key} marginBottom={2} display='flex' flexDirection='row' alignItems='flex-start'>
                <Box flex='1' paddingRight={2}>
                    <Typography variant='body2' fontWeight='medium' color='text.secondary'>
                        {field.label}:
                    </Typography>
                </Box>
                <Box flex='2'>
                    <Box display='flex' flexWrap='wrap' gap={1}>
                        {items.map((item, index) => {
                            let label = ''

                            if (typeof item === 'object' && item !== null) {
                                label = item.name ?? item.label ?? item.title ?? item.description ?? (item.uuid ? `${field.label} (${item.uuid.slice(-8)})` : JSON.stringify(item))
                            } else {
                                label = String(item)
                            }

                            return (
                                <Chip
                                    key={index}
                                    label={label}
                                    size='small'
                                    variant='outlined'
                                    color='primary'
                                />
                            )
                        })}
                    </Box>
                </Box>
            </Box>
        )
    }

    const renderFileCard = (file: IUploadedFile) => {
        const isFileLoading = fileLoading === file.key

        const handleFileClick = async () => {
            try {
                setFileLoading(file.key)
                executeFileDownload(file.key)
            } catch (error) {
                snackbar.show({ severity: 'error', text: 'Error fetching file URL: ' + error })
            } finally {
                setTimeout(() => setFileLoading(null), 1000)
            }
        }

        return (
            <SmartCard key={file.uuid} sx={{ minWidth: 250, maxWidth: 250, position: 'relative' }}>
                <CardActionArea onClick={handleFileClick} disabled={isFileLoading}>
                    <CardContent>
                        {isFileLoading && (
                            <CardActionArea
                                sx={{ padding: 1, position: 'absolute' }}
                                disabled>
                                <Box
                                    flexDirection='row'
                                    display='flex'
                                    gap={1}
                                    alignItems='center'
                                    justifyContent='center'>
                                    <CircularProgress />
                                </Box>
                            </CardActionArea>
                        )}
                        <Box display='flex' alignItems='center' gap={2}>
                            <PictureAsPdfRoundedIcon color='primary' sx={{ fontSize: 40 }} />
                            <Box flex={1} overflow='hidden'>
                                <Typography variant='body2' fontWeight='bold' noWrap>
                                    {file.name}
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                    {new Date(file.uploadedAt).toLocaleDateString()} • {(file.size / 1024).toFixed(1)} KB
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </CardActionArea>
            </SmartCard>
        )
    }

    const contractFiles = (files ?? []).filter(x => x?.contractId === contractId)

    if (!contract && !contractSchema && !contractSchema?.steps) {
        return (
            <Tile.Container>
                <Skeleton variant='rectangular' />
            </Tile.Container>
        )
    }

    return (
        <Tile.Container>
            <Tile.Column>
                <Box>
                    <Box sx={{ marginBottom: 2 }} display='flex' gap={1.5}>
                        <SmartCard sx={{ padding: 2 }}>
                            <Stack direction='row' spacing={2} display='flex'>
                                <TextField
                                    variant='outlined'
                                    size='small'
                                    label='Contract ID'
                                    value={contract?.id}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            'borderRadius': '25px',
                                            '& fieldset': {
                                                borderRadius: '25px'
                                            }
                                        }
                                    }}
                                />
                                <Button size='small' color='secondary' variant='text' onClick={handleCopyID}>Copy</Button>
                            </Stack>
                        </SmartCard>
                        <SmartCard sx={{ padding: 2 }}>
                            <Stack direction='row' spacing={2}>
                                <TextField
                                    variant='outlined'
                                    size='small'
                                    label='Business Key'
                                    value={contract?.externalId}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            'borderRadius': '25px',
                                            '& fieldset': {
                                                borderRadius: '25px'
                                            }
                                        }
                                    }}
                                />
                                <Button
                                    size='small'
                                    color='secondary'
                                    variant='text'
                                    onClick={handleCopyBusinessKey}>{copied ? 'Copied' : 'Copy'}
                                </Button>
                            </Stack>
                        </SmartCard>
                    </Box>
                    {contractSchema?.steps?.map((step, stepIndex) => (
                        <SmartCard key={stepIndex} sx={{ marginBottom: 2 }}>
                            <CardContent>
                                <Typography variant='h6' fontWeight='bold' gutterBottom>
                                    {step.label}
                                </Typography>
                                <Divider sx={{ marginBottom: 2 }} />

                                {step.fields.map(field => {
                                    const value = Helper.getNestedValue(contract, field.key)
                                    return renderField(field, value, 0)
                                })}
                            </CardContent>
                        </SmartCard>
                    ))}
                </Box>
                <Box>
                    <Typography variant='h6' gutterBottom>
                        Contract Files ({contractFiles.length})
                    </Typography>

                    {contractFiles.length === 0
                        ? (
                                <Paper sx={{ padding: 4, textAlign: 'center' }}>
                                    <Typography variant='body2' color='text.secondary'>
                                        No files attached to this contract.
                                    </Typography>
                                </Paper>
                            )
                        : (
                                <Box display='flex' gap={2} flexWrap='wrap'>
                                    {contractFiles.map(file => renderFileCard(file))}
                                </Box>
                            )}
                </Box>
            </Tile.Column>
        </Tile.Container>
    )
}

const SimpleField = ({ paduCompiled, field, value }: { field: IFormField, value: unknown, contract: IContract, paduCompiled: IPADUCompiled }) => {
    const formattedValue = Helper.formatValue(value, field.type)
    const paduStatus = paduCompiled?.fields?.find(x => x.key === (field as any).fullKey || x.key === field.key)
    const renderPADUChip = () => {
        if (!paduStatus || paduStatus.status === 'none') return null

        return (
            <PADUChip compiledPADU={paduStatus} />
        )
    }

    return (

        <Box key={field.key} marginBottom={2} display='flex' flexDirection='row' alignItems='flex-start'>
            <Box flex='1' paddingRight={2}>
                <Typography variant='body2' fontWeight='medium' color='text.secondary'>
                    {field.label}:
                </Typography>
            </Box>
            <Box flex='2' display='flex' flexDirection='row' alignItems='center' gap={1}>
                {renderPADUChip()}
                {field.type === 'long-form'
                    ? (
                            <TextField
                                variant='outlined'
                                multiline
                                disabled
                                fullWidth
                                value={formattedValue}
                                maxRows={4}
                                size='small'
                            />
                        )
                    : field.type === 'bool'
                        ? (
                                <Box display='flex' alignItems='center' gap={1}>
                                    {value === true
                                        ? (
                                                <CheckBoxRoundedIcon color='success' fontSize='small' />
                                            )
                                        : value === false
                                            ? (
                                                    <CancelIcon color='error' fontSize='small' />
                                                )
                                            : null}
                                    <Typography variant='body2'>{formattedValue}</Typography>
                                </Box>
                            )
                        : (
                                <Typography variant='body2'>{formattedValue}</Typography>
                            )}
            </Box>
        </Box>
    )
}

export default ContractDetailsViews
